import type {
  LayoutPoint,
  ViewerEntityId,
  ViewerPosition,
} from '../contracts/models';
import { normalizeLayoutPolygon } from './polygons';

export type AreaTagPointInput =
  | readonly [number, number]
  | { x: number; y: number };

export interface AreaTagDrawingPolygon<
  TId extends ViewerEntityId = ViewerEntityId,
> {
  id: TId;
  points: readonly AreaTagPointInput[];
}

export interface AreaTagDrawingSnap<
  TId extends ViewerEntityId = ViewerEntityId,
> {
  point: LayoutPoint;
  type: 'vertex' | 'edge';
  polygonId: TId;
}

export interface DrawingLineAssist {
  referenceLineIndex: number;
  angle?: number;
  length?: number;
}

export interface DrawingGeometricAssist {
  point: LayoutPoint;
  parallel: DrawingLineAssist | null;
  equalLength: DrawingLineAssist | null;
}

export const normalizeAreaTagPoint = (
  point: AreaTagPointInput,
): LayoutPoint | null => {
  const x = 'x' in point ? Number(point.x) : Number(point[0]);
  const y = 'y' in point ? Number(point.y) : Number(point[1]);
  return Number.isFinite(x) && Number.isFinite(y) ? [x, y] : null;
};

export const normalizeAreaTagPolygon = (
  points: readonly AreaTagPointInput[],
): LayoutPoint[] =>
  normalizeLayoutPolygon(
    points
      .map(normalizeAreaTagPoint)
      .filter((point): point is LayoutPoint => point !== null),
  );

export const isPointInLayoutPolygon = (
  point: LayoutPoint,
  polygon: readonly AreaTagPointInput[],
): boolean => {
  const points = normalizeAreaTagPolygon(polygon);
  if (points.length < 3) return false;
  const [x, y] = point;
  let inside = false;

  for (
    let index = 0, previous = points.length - 1;
    index < points.length;
    previous = index, index += 1
  ) {
    const [currentX, currentY] = points[index];
    const [previousX, previousY] = points[previous];
    if (
      (currentY > y) !== (previousY > y) &&
      x <
        ((previousX - currentX) * (y - currentY)) /
          (previousY - currentY) +
          currentX
    ) {
      inside = !inside;
    }
  }
  return inside;
};

export const findItemsInLayoutPolygon = <T>(
  items: readonly T[],
  polygon: readonly AreaTagPointInput[],
  getPoint: (item: T) => ViewerPosition,
): T[] =>
  items.filter((item) => {
    const point = getPoint(item);
    return isPointInLayoutPolygon([point.x, point.y], polygon);
  });

export const distanceBetweenLayoutPoints = (
  first: LayoutPoint,
  second: LayoutPoint,
): number => Math.hypot(second[0] - first[0], second[1] - first[1]);

export const closestPointOnLayoutSegment = (
  point: LayoutPoint,
  start: LayoutPoint,
  end: LayoutPoint,
): LayoutPoint => {
  const deltaX = end[0] - start[0];
  const deltaY = end[1] - start[1];
  const lengthSquared = deltaX ** 2 + deltaY ** 2;
  if (lengthSquared === 0) return start;
  const ratio = Math.max(
    0,
    Math.min(
      1,
      ((point[0] - start[0]) * deltaX +
        (point[1] - start[1]) * deltaY) /
        lengthSquared,
    ),
  );
  return [
    start[0] + ratio * deltaX,
    start[1] + ratio * deltaY,
  ];
};

export const distanceToLayoutSegment = (
  point: LayoutPoint,
  start: LayoutPoint,
  end: LayoutPoint,
): number =>
  distanceBetweenLayoutPoints(
    point,
    closestPointOnLayoutSegment(point, start, end),
  );

export const getAreaTagDrawingSnapThreshold = (
  scale: number,
): number => {
  const safeScale = Number.isFinite(scale) && scale > 0 ? scale : 1;
  return Math.max(4, Math.min(10, 8 / safeScale));
};

export const findAreaTagDrawingSnap = <
  TId extends ViewerEntityId = ViewerEntityId,
>(
  cursor: LayoutPoint,
  polygons: readonly AreaTagDrawingPolygon<TId>[],
  threshold: number,
): AreaTagDrawingSnap<TId> | null => {
  let bestDistance =
    Number.isFinite(threshold) && threshold > 0 ? threshold : 0;
  let bestSnap: AreaTagDrawingSnap<TId> | null = null;

  polygons.forEach((polygon) => {
    const points = normalizeAreaTagPolygon(polygon.points);
    if (points.length < 2) return;

    points.forEach((point) => {
      const distance = distanceBetweenLayoutPoints(cursor, point);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestSnap = {
          point,
          type: 'vertex',
          polygonId: polygon.id,
        };
      }
    });

    points.forEach((start, index) => {
      const end = points[(index + 1) % points.length];
      const distance = distanceToLayoutSegment(cursor, start, end);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestSnap = {
          point: closestPointOnLayoutSegment(cursor, start, end),
          type: 'edge',
          polygonId: polygon.id,
        };
      }
    });
  });

  return bestSnap;
};

const pointOnSegment = (
  point: LayoutPoint,
  start: LayoutPoint,
  end: LayoutPoint,
): boolean => {
  const cross =
    (point[1] - start[1]) * (end[0] - start[0]) -
    (point[0] - start[0]) * (end[1] - start[1]);
  if (Math.abs(cross) > 1e-10) return false;
  const dot =
    (point[0] - start[0]) * (end[0] - start[0]) +
    (point[1] - start[1]) * (end[1] - start[1]);
  if (dot < -1e-10) return false;
  const lengthSquared =
    (end[0] - start[0]) ** 2 + (end[1] - start[1]) ** 2;
  return dot - lengthSquared <= 1e-10;
};

const orientation = (
  first: LayoutPoint,
  second: LayoutPoint,
  third: LayoutPoint,
): number => {
  const value =
    (second[1] - first[1]) * (third[0] - second[0]) -
    (second[0] - first[0]) * (third[1] - second[1]);
  if (Math.abs(value) < 1e-10) return 0;
  return value > 0 ? 1 : 2;
};

const segmentsIntersect = (
  firstStart: LayoutPoint,
  firstEnd: LayoutPoint,
  secondStart: LayoutPoint,
  secondEnd: LayoutPoint,
): boolean => {
  const firstOrientation = orientation(
    firstStart,
    firstEnd,
    secondStart,
  );
  const secondOrientation = orientation(
    firstStart,
    firstEnd,
    secondEnd,
  );
  const thirdOrientation = orientation(
    secondStart,
    secondEnd,
    firstStart,
  );
  const fourthOrientation = orientation(
    secondStart,
    secondEnd,
    firstEnd,
  );
  if (
    firstOrientation !== secondOrientation &&
    thirdOrientation !== fourthOrientation
  ) {
    return true;
  }
  return (
    (firstOrientation === 0 &&
      pointOnSegment(secondStart, firstStart, firstEnd)) ||
    (secondOrientation === 0 &&
      pointOnSegment(secondEnd, firstStart, firstEnd)) ||
    (thirdOrientation === 0 &&
      pointOnSegment(firstStart, secondStart, secondEnd)) ||
    (fourthOrientation === 0 &&
      pointOnSegment(firstEnd, secondStart, secondEnd))
  );
};

const strictlyInsidePolygon = (
  point: LayoutPoint,
  polygon: readonly LayoutPoint[],
): boolean => {
  for (let index = 0; index < polygon.length; index += 1) {
    if (
      pointOnSegment(
        point,
        polygon[index],
        polygon[(index + 1) % polygon.length],
      )
    ) {
      return false;
    }
  }
  return isPointInLayoutPolygon(point, polygon);
};

/**
 * Detects interior overlap while allowing polygons to share boundaries and
 * vertices, matching the Ironview 360 authoring behavior.
 */
export const layoutPolygonsOverlap = (
  firstInput: readonly AreaTagPointInput[],
  secondInput: readonly AreaTagPointInput[],
): boolean => {
  const first = normalizeAreaTagPolygon(firstInput);
  const second = normalizeAreaTagPolygon(secondInput);
  if (first.length < 3 || second.length < 3) return false;

  for (let firstIndex = 0; firstIndex < first.length; firstIndex += 1) {
    const firstStart = first[firstIndex];
    const firstEnd = first[(firstIndex + 1) % first.length];
    for (
      let secondIndex = 0;
      secondIndex < second.length;
      secondIndex += 1
    ) {
      const secondStart = second[secondIndex];
      const secondEnd = second[(secondIndex + 1) % second.length];
      if (
        segmentsIntersect(
          firstStart,
          firstEnd,
          secondStart,
          secondEnd,
        ) &&
        !pointOnSegment(firstStart, secondStart, secondEnd) &&
        !pointOnSegment(firstEnd, secondStart, secondEnd) &&
        !pointOnSegment(secondStart, firstStart, firstEnd) &&
        !pointOnSegment(secondEnd, firstStart, firstEnd)
      ) {
        return true;
      }
    }
  }

  return (
    strictlyInsidePolygon(first[0], second) ||
    strictlyInsidePolygon(second[0], first)
  );
};

const getLineAngle = (
  start: LayoutPoint,
  end: LayoutPoint,
): number => Math.atan2(end[1] - start[1], end[0] - start[0]);

const angleDifference = (first: number, second: number): number => {
  let difference = Math.abs(first - second);
  if (difference > Math.PI) difference = 2 * Math.PI - difference;
  return difference;
};

const pointAtDistanceAndAngle = (
  start: LayoutPoint,
  distance: number,
  angle: number,
): LayoutPoint => [
  start[0] + distance * Math.cos(angle),
  start[1] + distance * Math.sin(angle),
];

export const applyDrawingGeometricAssist = (
  cursor: LayoutPoint,
  inputPoints: readonly AreaTagPointInput[],
  options: {
    parallel: boolean;
    equalLength: boolean;
    parallelTolerance?: number;
    lengthToleranceRatio?: number;
  },
): DrawingGeometricAssist => {
  const points = normalizeAreaTagPolygon(inputPoints);
  if (points.length === 0) {
    return { point: cursor, parallel: null, equalLength: null };
  }
  const lastPoint = points[points.length - 1];
  const cursorAngle = getLineAngle(lastPoint, cursor);
  const cursorLength = distanceBetweenLayoutPoints(lastPoint, cursor);
  const parallelTolerance = options.parallelTolerance ?? 0.017;
  const lengthToleranceRatio = options.lengthToleranceRatio ?? 0.1;
  let assistedPoint = cursor;
  let parallel: DrawingLineAssist | null = null;
  let equalLength: DrawingLineAssist | null = null;

  for (let index = 0; index < points.length - 1; index += 1) {
    const lineStart = points[index];
    const lineEnd = points[index + 1];
    const lineAngle = getLineAngle(lineStart, lineEnd);
    const lineLength = distanceBetweenLayoutPoints(lineStart, lineEnd);

    if (options.parallel && !parallel) {
      const oppositeAngle = lineAngle + Math.PI;
      const directDifference = angleDifference(cursorAngle, lineAngle);
      const oppositeDifference = angleDifference(
        cursorAngle,
        oppositeAngle,
      );
      if (
        Math.min(directDifference, oppositeDifference) <
        parallelTolerance
      ) {
        const bestAngle =
          directDifference < oppositeDifference
            ? lineAngle
            : oppositeAngle;
        parallel = { referenceLineIndex: index, angle: bestAngle };
        assistedPoint = pointAtDistanceAndAngle(
          lastPoint,
          cursorLength,
          bestAngle,
        );
      }
    }

    if (
      options.equalLength &&
      !equalLength &&
      lineLength > 0 &&
      Math.abs(cursorLength - lineLength) / lineLength <
        lengthToleranceRatio
    ) {
      equalLength = {
        referenceLineIndex: index,
        length: lineLength,
      };
      assistedPoint = pointAtDistanceAndAngle(
        lastPoint,
        lineLength,
        getLineAngle(lastPoint, assistedPoint),
      );
    }
  }

  return { point: assistedPoint, parallel, equalLength };
};
